import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X, Scale, FileUp } from "lucide-react";
import { useState, useEffect } from "react";
import { BUSINESS } from "@/lib/constants";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/consultation", label: "Free Consultation" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/reviews", label: "Reviews" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  useEffect(() => { setOpen(false); }, [location.pathname]);

  if (isAdmin) {
    return <div className="min-h-screen bg-slate-50">{children}</div>;
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Top trust bar */}
      <div className="bg-navy text-white text-sm sm:text-base py-2 px-4">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-center sm:justify-between gap-2">
          <span data-testid="topbar-rating">★ 4.9 Google Rating · 11 Years in Business</span>
          <span data-testid="topbar-hours" className="hidden sm:inline">Open Daily 8 AM – 10 PM</span>
        </div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-md bg-navy flex items-center justify-center">
              <Scale className="w-6 h-6 text-gold" strokeWidth={2.5} />
            </div>
            <div className="leading-tight">
              <div className="font-heading font-bold text-navy text-lg sm:text-xl">Traffic Tickets</div>
              <div className="font-heading font-bold text-navy text-lg sm:text-xl -mt-1">Protection INC</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-base font-medium transition-colors ${
                    isActive ? "text-navy bg-slate-100" : "text-gray-700 hover:text-navy hover:bg-slate-50"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              data-testid="header-call-btn"
              className="hidden md:inline-flex items-center gap-2 bg-gold text-black font-bold px-4 py-3 rounded-md hover:bg-[#B4952D] transition-colors"
            >
              <Phone className="w-5 h-5" />
              {BUSINESS.phone}
            </a>
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 rounded-md hover:bg-slate-100"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
            >
              {open ? <X className="w-7 h-7 text-navy" /> : <Menu className="w-7 h-7 text-navy" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t border-gray-200 bg-white">
            <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-1">
              {navItems.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `px-4 py-4 rounded-md text-lg font-medium ${
                      isActive ? "bg-slate-100 text-navy" : "text-gray-800"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <a
                href={`tel:${BUSINESS.phoneRaw}`}
                data-testid="mobile-call-btn"
                className="btn-primary mt-2"
              >
                <Phone className="w-5 h-5" /> {BUSINESS.phone}
              </a>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-navy text-white mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <h3 className="font-heading text-xl font-bold mb-3">{BUSINESS.name}</h3>
            <p className="text-slate-300 text-base leading-relaxed">
              Helping Alberta drivers fight traffic tickets for over 11 years.
              Most tickets we handle get reduced.
            </p>
          </div>
          <div>
            <h4 className="font-heading text-lg font-semibold mb-3 text-gold">Contact</h4>
            <p className="text-slate-200 text-base">
              <a href={`tel:${BUSINESS.phoneRaw}`} data-testid="footer-phone" className="hover:text-gold">
                {BUSINESS.phone}
              </a>
            </p>
            <p className="text-slate-200 text-base mt-2">{BUSINESS.address}</p>
            <p className="text-slate-200 text-base">{BUSINESS.city}</p>
            <p className="text-slate-200 text-base mt-2">{BUSINESS.hours}</p>
          </div>
          <div>
            <h4 className="font-heading text-lg font-semibold mb-3 text-gold">Quick Links</h4>
            <ul className="space-y-2 text-base">
              <li><Link to="/consultation" className="text-slate-200 hover:text-gold">Book Free Consultation</Link></li>
              <li><Link to="/services" className="text-slate-200 hover:text-gold">Services</Link></li>
              <li><Link to="/reviews" className="text-slate-200 hover:text-gold">Reviews</Link></li>
              <li><Link to="/faq" className="text-slate-200 hover:text-gold">FAQ</Link></li>
              <li><Link to="/contact" className="text-slate-200 hover:text-gold">Contact</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 text-sm text-slate-400 flex flex-col sm:flex-row justify-between gap-2">
            <span>© {new Date().getFullYear()} {BUSINESS.name}. All rights reserved.</span>
            <span>Serving {BUSINESS.serviceArea}</span>
          </div>
        </div>
      </footer>

      {/* Sticky mobile CTA — stacked so the buttons aren't hidden by the Emergent badge in the bottom-right corner */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-lg p-2 flex flex-col gap-2">
        <Link
          to="/consultation"
          data-testid="sticky-consult-btn"
          className="btn-primary text-base px-3 py-3 min-h-[48px] w-full"
        >
          <FileUp className="w-5 h-5" /> Book Free Consultation
        </Link>
        <a
          href={`tel:${BUSINESS.phoneRaw}`}
          data-testid="sticky-call-btn"
          className="btn-primary text-base px-3 py-3 min-h-[48px]"
          style={{ marginRight: "172px" }}
        >
          <Phone className="w-5 h-5" /> Call Now
        </a>
      </div>
      <div className="lg:hidden h-32" aria-hidden />
    </div>
  );
}
